import React, {FC, useState, useContext} from 'react';
import Box from '@material-ui/core/Box';

import {getLocale} from '@epic-core/common/dist';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {getAssetPath} from 'epic-diesel-common/src/utils/DieselUtils';
import {Players, StatusPendingReview} from '@epic-ui/icons';

import {SidebarPreviousDataContext} from '../SidebarPreviousDataProvider';
import StatisticsPanel from '../StatisticsPanel';
import StatisticsCard from '../StatisticsCard';
import MyTopPerformingGamesTrend from './MyTopPerformingGamesTrend';
import useMyTopPerformingGamesData from './useMyTopPerformingGamesData';
import MyTopPerformingGamesCardContent from './MyTopPerformingGamesCardContent';
import DataUnavailableTooltip from '../DataUnavailableTooltip';
import {MyTopPerformingGame} from '../../../types';
import {numberFormatter} from '../../../utils/FNUtils';
import {PDFPictureTrap} from '../../v2/sections/fortnite-games/export/PDFExportProvider';
import useDurationFormatter from '../../v2/sections/fortnite-games/hooks/useDurationFormatter';

// @ts-ignore
import starsIcon from '../../../../../assets/stars-icon.svg';
// @ts-ignore
import growthIcon from '../../../../../assets/growth-icon.svg';

const CODES = {
    [MyTopPerformingGame.MOST_POPULAR_GAME]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.my_most_popular_game',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.weekly_active_players_description'
    },
    [MyTopPerformingGame.HIGHEST_GROWTH]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.highest_growth',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.weekly_active_players_description'
    },
    [MyTopPerformingGame.HIGHEST_AVG_PLAY_TIME]: {
        title:
            'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.highest_avg_play_time',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.average_play_time_description'
    },
    [MyTopPerformingGame.MOST_VISITED_GAME]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.most_visited_game',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.total_lifetime_plays_description'
    },
    title: 'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.title',
    tooltip: 'epic.affiliate.fortnite_games.sidebar.my_top_performing_games.tooltip',

    dataUnavailableTitle: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_title',
    dataUnavailableDescription: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_description'
};

export interface MyOverallHighlightsPanelProps {
    getMessage: (code: string) => string;
}

const MyTopPerformingGamesPanel: FC<MyOverallHighlightsPanelProps> = ({
    getMessage
}: MyOverallHighlightsPanelProps) => {
    const [isTrendSelected, setIsTrendSelected] = useState<boolean>(false);

    const gamesData = useMyTopPerformingGamesData();

    const {
        prevMostPopularGame,
        prevHighestGrowth,
        prevHighestAvgPlayTime,
        prevMostVisitedGame
    } = useContext(SidebarPreviousDataContext);

    const formatDuration = useDurationFormatter();

    const games = {
        [MyTopPerformingGame.MOST_POPULAR_GAME]: {
            formatter: numberFormatter(getLocale()),
            previousData: prevMostPopularGame,
            // @ts-ignore
            icon: <Box component="img" src={getAssetPath(starsIcon)} />
        },
        [MyTopPerformingGame.HIGHEST_GROWTH]: {
            formatter: numberFormatter(getLocale()),
            previousData: prevHighestGrowth,
            // @ts-ignore
            icon: <Box component="img" src={getAssetPath(growthIcon)} />
        },
        [MyTopPerformingGame.HIGHEST_AVG_PLAY_TIME]: {
            formatter: formatDuration,
            previousData: prevHighestAvgPlayTime,
            icon: <StatusPendingReview color="disabled" />
        },
        [MyTopPerformingGame.MOST_VISITED_GAME]: {
            formatter: numberFormatter(getLocale()),
            previousData: prevMostVisitedGame,
            icon: <Players color="disabled" />
        }
    };

    const getValueType = (gameType: string) =>
        gameType === MyTopPerformingGame.HIGHEST_GROWTH && '+';

    const handleMouseEnter = () => setIsTrendSelected(true);
    const handleMouseLeave = () => setIsTrendSelected(false);

    return (
        <PDFPictureTrap id={getMessage(CODES.title)} index={6}>
            <StatisticsPanel title={getMessage(CODES.title)} tooltip={getMessage(CODES.tooltip)}>
                {Object.entries(gamesData).map(
                    ([game, {value, trend, creativeIslandCode, loading}]) => (
                        <StatisticsCard
                            key={game}
                            title={getMessage(CODES[game].title)}
                            value={
                                <MyTopPerformingGamesCardContent
                                    value={value}
                                    previousValue={games[game].previousData.value}
                                    code={creativeIslandCode}
                                    title={getMessage(CODES.dataUnavailableTitle)}
                                    tooltip={
                                        !value && (
                                            <Box mb={games[game].previousData.value ? -1 : 0}>
                                                <DataUnavailableTooltip
                                                    title={getMessage(CODES.dataUnavailableTitle)}
                                                    description={getMessage(
                                                        CODES.dataUnavailableDescription
                                                    )}
                                                />
                                            </Box>
                                        )
                                    }
                                />
                            }
                            trend={
                                <MyTopPerformingGamesTrend
                                    value={trend}
                                    valueType={getValueType(game)}
                                    isSelected={isTrendSelected}
                                    formatter={games[game].formatter}
                                />
                            }
                            tooltip={
                                !value &&
                                !trend &&
                                games[game].previousData.value && (
                                    <Box
                                        mt={0.4}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}>
                                        <DataUnavailableTooltip
                                            title={getMessage(CODES.dataUnavailableTitle)}
                                            description={getMessage(
                                                CODES.dataUnavailableDescription
                                            )}
                                        />
                                    </Box>
                                )
                            }
                            isLoading={loading}
                            description={getMessage(CODES[game].description)}>
                            {games[game].icon}
                        </StatisticsCard>
                    )
                )}
            </StatisticsPanel>
        </PDFPictureTrap>
    );
};

export default withLocalization(MyTopPerformingGamesPanel);
